import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { toReactFlowNode, toReactFlowEdge } from '@/services/canvas-mappers';
import type { Node, Edge } from '@xyflow/react';

export function useCanvasData(projectId: string) {
  return useQuery({
    queryKey: ['canvas', projectId],
    queryFn: async () => {
      const [nodesRes, edgesRes] = await Promise.all([
        api.listNodes(projectId),
        api.listEdges(projectId),
      ]);

      // A failed load must not render as an empty graph, and a partial one must
      // not render as nodes with no connections — both read as data loss on a
      // canvas the user has spent the journey building. Fail the whole query
      // instead so the caller can offer a retry.
      if (!nodesRes.success || !edgesRes.success) {
        throw new Error(
          nodesRes.message || edgesRes.message || 'Failed to load canvas',
        );
      }

      // Idea nodes with empty title are kept in DynamoDB to
      // preserve any incoming evidence edges across PM transient clears,
      // but they shouldn't render on the canvas — an "Idea: [empty]"
      // placeholder reads as broken UI. Edges touching a hidden node are
      // also dropped here so any evidence edge that
      // would target the empty idea node doesn't dangle (FlowCanvas's
      // edge-drop pass only triggers when source/segment filters are
      // active, so it can't be relied on for the unfiltered case).
      const rawNodes = nodesRes.data ?? [];
      const visibleDbNodes = rawNodes.filter(
        (n) => n.type !== 'idea' || (typeof n.title === 'string' && n.title.trim().length > 0),
      );
      const visibleNodeIds = new Set(visibleDbNodes.map((n) => n.id));

      const nodes: Node[] = visibleDbNodes.map(toReactFlowNode);

      const edges: Edge[] = (edgesRes.data ?? [])
        .filter((e) => visibleNodeIds.has(e.sourceNodeId) && visibleNodeIds.has(e.targetNodeId))
        .map(toReactFlowEdge);

      return { nodes, edges };
    },
    enabled: !!projectId,
  });
}
